import React, { useState, useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { FaArrowLeft } from "react-icons/fa";
import HeaderContainer from "../CrewDashboard/HeaderContainer";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import TextInput from "../common/TextInput";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

const DriverCredentials = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [certificationFile, setCertificationFile] = useState(null);
  const [certificationPreview, setCertificationPreview] = useState(null);

  const reduxDriverId = useSelector((state) => state.auth.driverId);
  const [numericDriverId, setNumericDriverId] = useState(null);
  
  // Debug the driver ID type and value
  console.log("DriverCredentials - reduxDriverId:", reduxDriverId, "type:", typeof reduxDriverId);
  
  useEffect(() => {
    const getNumericDriverId = async () => {
      if (!reduxDriverId) return;
      
      // If it's already a number, use it
      if (typeof reduxDriverId === 'number') {
        setNumericDriverId(reduxDriverId);
        return;
      }
      
      // If it's a UUID, look up the numeric ID from the Drivers table
      const { data, error } = await supabase
        .from("Drivers")
        .select("DriverID")
        .eq("auth_id", reduxDriverId)
        .single();
      
      if (error) {
        console.error("Error getting numeric driver ID:", error);
        return;
      }
      
      console.log("DriverCredentials - Found numeric ID:", data.DriverID);
      setNumericDriverId(data.DriverID);
    };
    
    getNumericDriverId();
  }, [reduxDriverId]);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      licenseNumber: "",
      licenseExpiry: "",
      tsaCheck: null,
      dmvStatus: null,
    },
  });

  const handleCertificationChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCertificationFile(file);
      const previewUrl = URL.createObjectURL(file);
      setCertificationPreview(previewUrl);
    }
  };

  const onSuccess = useCallback(() => {
    toast.success("Driver credentials saved!");
    // Check if user is admin or manager to determine where to navigate
    const userRole = sessionStorage.getItem("UserRole");
    if (userRole === "1") {
      navigate("/super-admin"); // Admin goes to super admin dashboard
    } else {
      navigate("/manager-drivers"); // Manager goes to their drivers list
    }
  }, [navigate]);

  const onError = useCallback((error) => {
    console.error("Error saving credentials:", error);
    toast.error("Failed to save driver credentials.");
  }, []);

  const handleSave = useCallback(
    async (formData) => {
      try {
        setLoading(true);

        let certificationImageUrl = "";

        if (certificationFile) {
          const fileExt = certificationFile.name.split(".").pop();
          const fileName = `cert_${Date.now()}.${fileExt}`;
          const filePath = `driver-photos/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from("driver-photos")
            .upload(filePath, certificationFile);

          if (uploadError) throw uploadError;

          const { data } = supabase.storage
            .from("driver-photos")
            .getPublicUrl(filePath);

          certificationImageUrl = data.publicUrl;
        }

        if (!numericDriverId) {
          throw new Error("Driver ID not available. Please try again.");
        }
        
        console.log("DriverCredentials - Using numeric ID for insert:", numericDriverId);
        const { error } = await supabase.from("DriverCredentials").insert([
          {
            DriverID: numericDriverId,
            Number: formData.licenseNumber,
            Expiration: formData.licenseExpiry,
            // TSABackgroundCheck: formData.tsaCheck,
            Photo: certificationImageUrl,
            // DMVRecordStatus: formData.dmvStatus,
          },
        ]);

        if (error) throw error;

        onSuccess();
      } catch (error) {
        onError(error);
      } finally {
        setLoading(false);
      }
    },
    [numericDriverId, certificationFile, onSuccess, onError]
  );

  const handleBack = () => {
    navigate("/driver-new");
  };

  const handleClear = () => {
    reset({
      licenseNumber: "",
      licenseExpiry: "",
      tsaCheck: null,
      dmvStatus: null,
    });
    setCertificationFile(null);
    setCertificationPreview(null);
  };

  return (
    <div className="p-6 w-full">
      <HeaderContainer />
      {/* Top Section */}
      <div className="items-center mt-5 personal-info-content">
        <div
          onClick={handleBack}
          className="flex cursor-pointer justify-between items-center w-20 back-button"
        >
          <FaArrowLeft />
          <span>Back</span>
        </div>
        <p className="pt-4 font-semibold text-lg">Driver Credentials</p>
        <span>Update your driver credentials to ensure smooth operations</span>

        <div className="w-100 mt-4 rounded-lg">
          <div className="flex justify-between mb-2">
            <div>
              <p>Driver's License Number</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="licenseNumber"
                rules={{
                  required: "License number is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.licenseNumber?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>License Expiry Date</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="licenseExpiry"
                rules={{
                  required: "License expiry date is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <TextInput
                    type="date"
                    value={value}
                    onChange={onChange}
                    className="custom-input rounded-lg w-100"
                    error={errors.licenseExpiry?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>TSA Background Check</p>
            </div>
            <div className="flex w-35">
              <Controller
                control={control}
                name="tsaCheck"
                rules={{
                  required: "TSA background check status is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex">
                    <label className="flex mr-1 items-center">
                      <input
                        type="radio"
                        checked={value === true}
                        onChange={() => onChange(true)}
                        className="mr-1"
                      />
                      Yes
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={value === false}
                        onChange={() => onChange(false)}
                        className="mr-1"
                      />
                      No
                    </label>
                  </div>
                )}
              />
              {errors.tsaCheck && (
                <span className="text-red-500 text-sm">
                  {errors.tsaCheck.message}
                </span>
              )}
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>Driver Certification</p>
            </div>
            <div className="flex justify-between w-35">
              <input
                type="file"
                accept="image/*"
                onChange={handleCertificationChange}
                className="custom-input rounded-lg w-100"
              />
              {certificationPreview && (
                <img
                  src={certificationPreview}
                  alt="Certification Preview"
                  className="mt-2 rounded-lg"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                  }}
                />
              )}
            </div>
          </div>

          <div className="flex justify-between mb-2">
            <div>
              <p>DMV Record Status</p>
            </div>
            <div className="flex justify-between w-35">
              <Controller
                control={control}
                name="dmvStatus"
                rules={{
                  required: "DMV record status is required",
                }}
                render={({ field: { value, onChange } }) => (
                  <div className="flex">
                    <label className="flex mr-1 items-center">
                      <input
                        type="radio"
                        checked={value === true}
                        onChange={() => onChange(true)}
                        className="mr-1"
                      />
                      Clear
                    </label>
                    <label className="flex mr-1 items-center">
                      <input
                        type="radio"
                        checked={value === false}
                        onChange={() => onChange(false)}
                        className="mr-1"
                      />
                      Pending
                    </label>
                  </div>
                )}
              />
              {errors.dmvStatus && (
                <span className="text-red-500 text-sm">
                  {errors.dmvStatus.message}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-4 mt-4 save-button">
          <button
            disabled={loading}
            onClick={handleSubmit(handleSave)}
            className="justify-between p-2 pr-20 pl-20 rounded-lg mr-1 flex items-center custom-blue-button"
          >
            <span>Save and Continue</span>
          </button>
          <div>
            <div
              onClick={handleClear}
              className="justify-between pl-20 pr-20 p-2 rounded-lg flex items-center custom-white-button"
            >
              <span>Clear</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DriverCredentials;
